import { type ReactNode, useMemo, useState } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarStack } from '@visx/shape';
import { useTooltipInPortal, useTooltip } from '@visx/tooltip';
import classNames from 'classnames';
import { ChartAverageMarker, type ChartMarker } from './ChartAverageMarker';
import useScreenSize from '../../hooks/useScreenSize';
import { getDateAxisTicks } from '../../utils/charts';
import { formatWithNumeral } from '../../utils/numbers';
import LoadingSpinner from '../LoadingSpinner';

export type BarDatumRecord = Record<string, string | number>;

type TooltipData = {
  dateIndex: number;
  datum: BarDatumRecord;
};

const DEFAULT_MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };

export const tooltipContainerClassName =
  'rounded-md border border-cf-gray-4 bg-cf-gray-3 bg-opacity-70 p-5 shadow-[0_4px_30px_0_rgba(0,0,0,0.5)] backdrop-blur-[12.5px]';

export type BarChartProps = {
  data: BarDatumRecord[];
  keys: string[];
  barColors: string[];
  chartId: string;
  chartDates: string[];
  xKey?: string;
  tooltipClassName?: string;
  margin?: { top: number; right: number; bottom: number; left: number };
  padding?: number;
  gridStroke?: string;
  axisTickColor?: string;
  axisTickFontSize?: number;
  barStroke?: string;
  gradientOpacityEnds?: number[];
  xTickFormat?: (dateString: string) => string;
  xTickValues?: string[];
  yTickFormat?: (value: string | number) => string;
  yAxisLabel?: string;
  yAxisLabelOffset?: number;
  renderTooltip: (datum: BarDatumRecord) => ReactNode;
  renderSvgOverlay?: (params: {
    xScale: ReturnType<typeof scaleBand<string>>;
    yMax: number;
    xMax: number;
    zeroY: number;
    yTickCount: number;
    data: BarDatumRecord[];
  }) => ReactNode;
  markers?: ChartMarker[];
  hideZero?: boolean;
};

const BarChartInner = ({
  width,
  height,
  data,
  keys,
  barColors,
  chartId,
  chartDates,
  xKey = 'date',
  tooltipClassName = tooltipContainerClassName,
  margin = DEFAULT_MARGIN,
  padding = 0.35,
  gridStroke = '#424242',
  axisTickColor = '#909090',
  axisTickFontSize = 12,
  barStroke,
  gradientOpacityEnds,
  xTickValues: xTickValuesProp,
  xTickFormat = (dateString: string) =>
    new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),

  renderSvgOverlay,
  markers,
  yTickFormat = (value: string | number) => String(formatWithNumeral(value, false, true)),
  yAxisLabel,
  yAxisLabelOffset = 40,
  hideZero = true,
  renderTooltip,
}: BarChartProps & { width: number; height: number }) => {
  const gradientId = (i: number) => `bar-gradient-${chartId}-${i}`;
  const { isMobile } = useScreenSize();
  const [focusedDateIndex, setFocusedDateIndex] = useState(-1);
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const normalizedData = useMemo(
    () =>
      data.map((d) => ({
        ...Object.fromEntries(keys.map((key) => [key, 0])),
        ...d,
      })),
    [data, keys],
  );

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: normalizedData.map((d) => d[xKey] as string),
        range: [0, xMax],
        padding,
      }),
    [normalizedData, xKey, xMax, padding],
  );

  const yScale = useMemo(() => {
    const values = normalizedData.map((d) =>
      keys.reduce((sum, key) => sum + Number(d[key] ?? 0), 0),
    );
    const minValue = Math.min(0, ...values);
    const maxValue = Math.max(0, ...values);
    return scaleLinear<number>({
      domain: [minValue, maxValue * 1.2 || 1],
      range: [yMax, 0],
      nice: true,
    });
  }, [normalizedData, keys, yMax]);

  const colorScale = useMemo(
    () => scaleOrdinal<string, string>({ domain: keys, range: barColors }),
    [keys, barColors],
  );

  const dateIndexMap = useMemo(
    () => new Map(chartDates.map((date, index) => [date, index])),
    [chartDates],
  );

  const tickValues = useMemo(
    () => xTickValuesProp ?? getDateAxisTicks(chartDates),
    [xTickValuesProp, chartDates],
  );

  // this is rendered as an approximate value
  const yTickCount = useMemo(() => {
    const tickLength = yScale.ticks().length;
    return tickLength > 5 ? 5 : tickLength;
  }, [yScale]);

  const defaultGradientOpacityEnd = keys.length > 1 ? 0.6 : 0.4;

  if (width < 10 || height < 10) return null;
  return (
    <div style={{ position: 'relative' }}>
      <svg ref={containerRef} width={width} height={height} role="application">
        <defs>
          {barColors.map((color, i) => (
            <linearGradient key={keys[i]} id={gradientId(i)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop
                offset="100%"
                stopColor={color}
                stopOpacity={gradientOpacityEnds?.[i] ?? defaultGradientOpacityEnd}
              />
            </linearGradient>
          ))}
        </defs>
        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={xMax}
            numTicks={yTickCount}
            stroke={gridStroke}
            strokeWidth={1}
            strokeDasharray="2"
          />
          <BarStack<BarDatumRecord, string>
            data={normalizedData}
            keys={keys}
            x={(d) => d[xKey] as string}
            xScale={xScale}
            yScale={yScale}
            color={colorScale}
            offset="diverging"
          >
            {(barStacks) =>
              barStacks.map((barStack, stackIndex) =>
                barStack.bars
                  .filter((bar) => bar.height > 0)
                  .map((bar) => (
                    <rect
                      key={`bar-stack-${barStack.index}-${bar.index}`}
                      x={bar.x}
                      y={bar.y}
                      height={bar.height}
                      width={bar.width}
                      fill={`url(#${gradientId(stackIndex)})`}
                      stroke={barStroke}
                      strokeWidth={barStroke ? 0.5 : undefined}
                      opacity={focusedDateIndex === -1 || focusedDateIndex === bar.index ? 1 : 0.3}
                    />
                  )),
              )
            }
          </BarStack>
          {/* Hover interaction layer */}
          {normalizedData.map((d) => {
            const dateStr = d[xKey] as string;
            const dateIndex = dateIndexMap.get(dateStr) ?? -1;
            const barX = xScale(dateStr);
            if (barX === undefined) return null;
            const singleSidePadding =
              normalizedData.length > 1 ? (xScale.step() - xScale.bandwidth()) / 2 : 0;
            return (
              <rect
                key={`hover-${dateStr}`}
                x={barX - singleSidePadding}
                y={0}
                height={yMax}
                width={xScale.bandwidth() + singleSidePadding * 2}
                fill="transparent"
                onMouseEnter={(event) => {
                  const coords = localPoint(event);
                  if (!coords) return;
                  setFocusedDateIndex(dateIndex);
                  showTooltip({
                    tooltipLeft: coords.x,
                    tooltipTop: coords.y,
                    tooltipData: { dateIndex, datum: d },
                  });
                }}
                onMouseMove={(event) => {
                  const coords = localPoint(event);
                  if (!coords) return;
                  showTooltip({
                    tooltipLeft: coords.x,
                    tooltipTop: coords.y,
                    tooltipData: { dateIndex, datum: d },
                  });
                }}
                onMouseLeave={() => {
                  setFocusedDateIndex(-1);
                  hideTooltip();
                }}
              />
            );
          })}
          {renderSvgOverlay?.({
            xScale,
            yMax,
            xMax,
            zeroY: yScale(0),
            yTickCount,
            data: normalizedData,
          })}
          {markers?.map((marker) => (
            <ChartAverageMarker
              key={marker.label}
              marker={marker}
              y={yScale(marker.value)}
              xMax={xMax}
            />
          ))}
          <AxisBottom
            top={yMax}
            scale={xScale}
            tickValues={tickValues}
            tickFormat={xTickFormat}
            stroke="transparent"
            tickStroke="transparent"
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: axisTickFontSize,
              angle: isMobile ? -60 : 0,
              textAnchor: isMobile ? 'end' : 'middle',
              dy: isMobile ? '-0.10em' : '0.50em',
              dx: isMobile ? '0.33em' : 0,
            }}
          />
          <AxisLeft
            scale={yScale}
            numTicks={yTickCount}
            hideZero={hideZero}
            tickFormat={(value) => yTickFormat(value as number)}
            stroke="transparent"
            tickStroke="transparent"
            label={yAxisLabel}
            labelOffset={yAxisLabelOffset}
            labelProps={{
              fill: axisTickColor,
              fontSize: 12,
              fontFamily: 'Aeonik-Medium',
              textAnchor: 'middle',
            }}
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: axisTickFontSize,
              textAnchor: 'end',
              dx: '-0.25em',
              dy: '0.33em',
            }}
          />
        </Group>
      </svg>
      {tooltipOpen &&
        tooltipData &&
        (isMobile ? (
          <div className="pointer-events-none fixed inset-0 z-10 flex items-center justify-center">
            <div className={tooltipClassName}>{renderTooltip(tooltipData.datum)}</div>
          </div>
        ) : (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            unstyled
            applyPositionStyle
            className="pointer-events-none z-10"
          >
            <div className={tooltipClassName}>{renderTooltip(tooltipData.datum)}</div>
          </TooltipInPortal>
        ))}
    </div>
  );
};

export const BarChart = ({
  isLoading,
  className = 'h-[350px]',
  ...props
}: BarChartProps & { isLoading: boolean; className?: string }) => (
  <div className={classNames(className, 'relative')}>
    {isLoading ? (
      <>
        <img
          src="/bar-chart-placeholder.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50 blur-sm"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </>
    ) : (
      <ParentSize>
        {({ width, height }) => <BarChartInner {...props} width={width} height={height} />}
      </ParentSize>
    )}
  </div>
);
